'use client';

import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { DateTime } from 'luxon';
import Payment from 'payment';
import { useLocation } from 'react-router';
import { SetURLSearchParams } from 'react-router-dom';
import {
  ErrorToast,
  ProviderStatusComponent,
  SuccessToast,
} from '../adminComponents';
import {
  allyLogo,
  bytesIcon,
  chowNowIcon,
  cutcatsIcon,
  cutcatsLogo,
  dedicatedDriver,
  dedicatedDriverIcon,
  deliveryNowIcon,
  deliveryNowLogo,
  deliveryIcon,
  devourNowIcon,
  dlivrdIcon,
  dlivrdLogo,
  doordashIcon,
  doordashLogo,
  dspIcon,
  dspLogo,
  eatstreetIcon,
  gloriaIcon,
  grubhubIcon,
  grubhubLogo,
  hyperzodIcon,
  kanekt365Icon,
  localForYouIcon,
  menufyIcon,
  motoclickIcon,
  motoclickLogo,
  nabeIcon,
  noFleetPhoto,
  ProviderConnectionConnectedIcon,
  ProviderConnectionDisabledIcon,
  ProviderConnectionPendingIcon,
  redwagonIcon,
  redwagonLogo,
  relayIcon,
  relayLogo,
  uberIcon,
  uberLogo,
  vdriveFleetIcon,
  vdriveFleetLogo,
  vGrubsAIIcon,
  vGrubsProviderIcon,
  vGrubsProviderLogo,
} from '../assets';
import { isFleetHidden } from '../store/demoMode';
import {
  ADMIN_DASHBOARD,
  ADMIN_DELIVERIES,
  ADMIN_LOCATIONS,
  ADMIN_LOGIN,
  ADMIN_ORDERS,
  ADMIN_USERS,
  PARTNER_LOCATIONS_BUSINESS_LICENSE,
  PARTNER_LOCATIONS_CONFIRM_LOCATION,
  PARTNER_CHAT,
  PARTNER_LOCATIONS_POS_SETUP,
  PARTNER_LOCATIONS_SELECT_PROVIDERS,
  PARTNER_SIGN_UP_ADD_LOCATION,
  PARTNER_SIGN_UP_BUSINESS_LICENSE,
  PARTNER_SIGN_UP_CONFIRM_LOCATION,
  PARTNER_SIGN_UP_POS_SETUP,
  PARTNER_SIGN_UP_SELECT_PROVIDERS,
  PARTNER_SIGN_UP_TERMS,
  PARTNER_SIGN_UP_USER_INFO,
} from '../routes';
import { lightTheme } from '../styles';
import { MenuNamesResponse } from '../types/api/menu.types';
import { BrandType } from '../types/brand.types';
import { GroupByType } from '../types/brandOwner/dashboard.brandOwner-types';
import {
  DayOfWeek,
  FundedTipScheduleEntity,
  ScheduleEntity,
  WeekDay,
} from '../types/location.types';
import {
  AdvancedQueryTypes,
  Provider,
  ProviderConnectionStatus,
  ProviderStatus,
  VDriveProvider,
} from '../types/shared.types';
import { UserRole } from '../types/user.types';
import {
  APP_TYPE,
  isAdmin,
  isBrandOwner,
  isDeliveries,
  isPartner,
  isPushApp,
  isSales,
} from './appType';
import { showsProviderIdentity } from './providerIdentity';
import {
  providers,
  renderPosImage,
  renderPosName,
  renderProviderImage,
  renderProviderName,
} from './providerLook';
// Defined in providerLook, a leaf the marketing site can import; exported here
// too, so every existing import keeps working.
export {
  providers,
  renderPosImage,
  renderPosName,
  renderProviderImage,
  renderProviderName,
};

import { DeliveryAssignmentEntity } from '../types/location.types';
import { PROTOCOL } from '../types/partner/api/wallet.partner-types';
import {
  PosConnectionType,
  ProviderConnectionType,
} from '../types/api/location.types';
import {
  OrderVDriveQuoteHistoryEntity,
  VDriveSource,
} from '../types/deliveries.types';
import { VirtualItem, Virtualizer } from '@tanstack/react-virtual';
import {
  AgentIncomeStatus,
  AgentIncomeType,
  IncomeStatus,
  IncomeType,
} from '../types/api/wallet.types';

export const isDesktop = () => {
  const query = `(min-width: 800px)`;
  return window.matchMedia(query).matches;
};

export const isMobile = () => {
  const query = `(max-width: 800px)`;
  return window.matchMedia(query).matches;
};

export const isDesktopBig = () => {
  const query = `(min-width: 3500px)`;
  return window.matchMedia(query).matches;
};

// Routes that restore their own scroll position (infinite-scroll lists).
// ScrollToTop must NOT reset these, or it would clobber the saved position.
const SCROLL_MANAGED_PATHS = [
  ADMIN_ORDERS,
  ADMIN_DELIVERIES,
  ADMIN_LOCATIONS,
  ADMIN_USERS,
];

const isScrollManaged = (pathname: string) =>
  SCROLL_MANAGED_PATHS.some((p) => pathname === p);

export const ScrollToTop = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  useLayoutEffect(() => {
    if (isScrollManaged(location.pathname)) return;
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return children;
};

export const useScrollToTop = (): [
  RefObject<HTMLDialogElement | HTMLDivElement>,
  () => void
] => {
  const scrollRef = useRef<HTMLDialogElement | HTMLDivElement>(null);

  const scrollToTop = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  return [scrollRef, scrollToTop];
};

export const useOnClickOutside = (
  ref: RefObject<HTMLDialogElement>,
  handler: (event: any) => void
) => {
  useEffect(() => {
    const listener = (event: any) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

export const t = 'true';

export const useWindowWide = (size: number) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    function handleResize() {
      setWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [setWidth]);

  return width > size;
};

export const Env = process.env;
export const facebookLink = Env.REACT_APP_FACEBOOK_LINK;
export const instagramLink = Env.REACT_APP_INSTAGRAM_LINK;
export const linkedInLink = Env.REACT_APP_LINKED_IN_LINK;

export const isProd = process.env.NX_APP_ENV === 'prod';

export const WIDGET_FORM_URL = 'https://api.leadconnectorhq.com/widget/form/';
export const WIDGET_BOOKING_URL =
  'https://api.leadconnectorhq.com/widget/booking/';
export const ROMAN_ID = '50AvXgnzHA4vHISzjvg7';
export const YURIY_ID = 'E8LmPXN1Smn9tGuQx1Tr';

export const PRICING_PLAN_UNIVERSAL_ID = 'kERd5xlNow6xzfuH314r';
export const PRICING_PLAN_BOOSTED_ID = 'o0rmSACICouZKHveLHBi';
export const PRICING_PLAN_SUPERCHARGED_ID = 'biw7ZUnb5PjdyNndBlsS';

export const UNIVERSAL = 'kERd5xlNow6xzfuH314r';
export const BOOSTED = 'o0rmSACICouZKHveLHBi';
export const SUPERCHARGED = 'biw7ZUnb5PjdyNndBlsS';

export const I_FRAME_FORM_STYLE = {
  border: 'none',
  width: '100%',
  height: 900,
};

export const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  unitDisplay: 'short',
  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});

export const formatterDollarShort = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  compactDisplay: 'short',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});

export const formatNumber = (
  value: number,
  options: Intl.NumberFormatOptions = {}
) =>
  new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  }).format(value);

export const displayCentsInDollars = (cents: number | null) =>
  formatter.format(cents ? cents / 100 : 0);

export const displayCentsInDollarsShort = (cents: number) =>
  formatterDollarShort.format(cents ? cents / 100 : 0);
export const displayPercentage = (p: number) => `${p}%`;

export function removeObjectWithId<T extends { id: string | number }>(
  arr: T[],
  id: string | number
): T[] {
  return arr.filter((obj) => obj.id !== id);
}

function preloadImage(src: string): Promise<void | HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = function () {
      resolve(img);
    };
    img.onerror = img.onabort = function () {
      reject(src);
    };
    img.src = src;
  });
}

export default function useImagePreloader(imageList: string[]) {
  const [imagesPreloaded, setImagesPreloaded] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    async function effect() {
      if (isCancelled) {
        return;
      }

      const imagesPromiseList: any = [];
      for (const i of imageList) {
        imagesPromiseList.push(preloadImage(i));
      }

      await Promise.all(imagesPromiseList);

      if (isCancelled) {
        return;
      }

      setImagesPreloaded(true);
    }

    effect();

    return () => {
      isCancelled = true;
    };
  }, [imageList]);

  return { imagesPreloaded };
}

export const pathname =
  typeof window !== 'undefined' ? window.location.pathname : '';

export const pathnameStartWith = (path: string) =>
  pathname.startsWith(path) ? pathname : null;

export const formatTime = (iso: string) => {
  const date_str = iso,
    options = {
      time: 'short' as const,
      hour: 'numeric' as const,
      minute: 'numeric' as const,
      // dayPeriod: 'narrow',
    },
    formatted = new Date(date_str).toLocaleTimeString('en-US', options);

  return formatted;
};

export const formatDateUTC = (iso: string) => {
  const date_str = iso,
    options = {
      timeZone: 'UTC' as const,
      year: 'numeric' as const,
      month: 'short' as const,
      day: 'numeric' as const,
    },
    formatted = new Date(date_str).toLocaleDateString('en-US', options);

  return formatted;
};

export const formatDateTimeFull = (dateString: string, timeZone?: string) => {
  const date = DateTime.fromISO(dateString).setZone(timeZone);
  if (!date.isValid) return '-';
  const hour = date.hour;
  const isPM = hour >= 12;
  const hourFormat = isPM ? 'h' : 'hh';
  return date.toFormat(`LLL dd, yyyy, ${hourFormat}:mm a`);
};

export const dateFormat = (dateString: string) => {
  const date = DateTime.fromISO(dateString);
  return date.toFormat(`LLL dd, yyyy`);
};

export const formatDateTimeShort = (dateString: string, timeZone?: string) => {
  const date = DateTime.fromISO(dateString).setZone(timeZone);
  if (!date.isValid) return '-';
  const hour = date.hour;
  const isPM = hour >= 12;
  const hourFormat = isPM ? 'h' : 'hh';
  return date.toFormat(`LLL dd, ${hourFormat}:mm a`);
};

// Defined in a leaf module and re-exported here so existing imports keep
// working. Eagerly-loaded code must import them from './appType' directly -
// reaching them through this file drags in `../routes` and closes an import
// cycle. See appType.ts.
export {
  APP_TYPE,
  isAdmin,
  isPartner,
  isSales,
  isBrandOwner,
  isDeliveries,
  isPushApp,
};

export const API_URL_PARTNER = process.env.REACT_APP_API_URL;
export const API_URL_ADMIN = process.env.REACT_APP_API_URL_ADMIN;
export const API_URL_SALES = process.env.REACT_APP_API_URL_SALES;
export const INITIAL_PAGINATION_PAGE = 1;
export const PAGINATION_LIMIT = 20;
export const handleShowPaginationButton = (arrayLength: number, page: number) =>
  arrayLength === PAGINATION_LIMIT * page;

export const handleSetUrlSearchParams = (
  key: string,
  value: string | number | null,
  setSearchParams: SetURLSearchParams,
  replace = false
) => {
  setSearchParams(
    (prevParams) => {
      if (value === null || value === undefined || value === '') {
        prevParams.delete(key);
      } else {
        prevParams.set(key, value.toString());
      }
      return prevParams;
    },
    { replace }
  );
};

export const renderMonthName = (month: number) => {
  switch (month) {
    case 1:
      return 'Jan';
    case 2:
      return 'Feb';
    case 3:
      return 'Mar';
    case 4:
      return 'Apr';
    case 5:
      return 'May';
    case 6:
      return 'Jun';
    case 7:
      return 'Jul';
    case 8:
      return 'Aug';
    case 9:
      return 'Sep';
    case 10:
      return 'Oct';
    case 11:
      return 'Nov';
    case 12:
      return 'Dec';

    default:
      break;
  }
};

export const setDocumentTitle = (pageTitle: string) =>
  (document.title = `${pageTitle}`);

function clearNumber(value = '') {
  return value.replace(/\D+/g, '');
}

export function formatCreditCardNumber(value: string) {
  if (!value) {
    return value;
  }

  const issuer = Payment.fns.cardType(value);
  const clearValue = clearNumber(value);
  let nextValue;

  switch (issuer) {
    case 'amex':
      nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
        4,
        10
      )} ${clearValue.slice(10, 15)}`;
      break;
    case 'dinersclub':
      nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
        4,
        10
      )} ${clearValue.slice(10, 14)}`;
      break;
    default:
      nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
        4,
        8
      )} ${clearValue.slice(8, 12)} ${clearValue.slice(12, 19)}`;
      break;
  }

  return nextValue.trim();
}

export function formatCVC(value: string, prevValue: string, allValues = {}) {
  const clearValue = clearNumber(value);
  const maxLength = 3;

  // if (allValues.number) {
  //   const issuer = Payment.fns.cardType(allValues.number);
  // }

  return clearValue.slice(0, maxLength);
}

export function formatExpirationDate(value: string) {
  const clearValue = clearNumber(value);

  if (clearValue.length >= 3) {
    return `${clearValue.slice(0, 2)}/${clearValue.slice(2, 4)}`;
  }

  return clearValue;
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const useDisableBodyScroll = (open: boolean) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [open]);
};

export const renderRole = (
  role: UserRole | null | undefined | string,
  representable?: boolean
) => {
  if (!role) return '';
  switch (role) {
    case UserRole.admin:
      return 'Admin';
    case UserRole.support:
      return 'Support';
    case UserRole.partner:
      return representable ? 'Merchant' : 'Partner';
    case UserRole.manager:
      return 'Manager';
    case UserRole.salesAgent:
      return 'Sales Agent';
    case UserRole.salesManager:
      return 'Sales Manager';
    case UserRole.staff:
      return 'Staff';
    case UserRole.marketing:
      return 'Marketing';
    case UserRole.brandOwner:
      return representable ? 'Brands' : 'Brand Owner';
    case UserRole.deliveries:
      return representable ? 'vDrive' : 'Deliveries Manager';
    default:
      return '';
  }
};

export const setTimeTo = (date: string | Date, type: 'start' | 'end') => {
  const dt = new Date(date);
  dt.setHours(type === 'start' ? 0 : 23);
  dt.setMinutes(type === 'start' ? 0 : 59);
  dt.setSeconds(type === 'start' ? 0 : 59);
  dt.setMilliseconds(type === 'start' ? 0 : 59);
  return dt;
};

export const isToday = (date: Date) => {
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};

export function centsToDollars(cents: number | null) {
  if (!cents) return 0;
  // Divide cents by 100 to get dollars
  return cents / 100;
}

export function dollarsToCents(dollars: number) {
  // Multiply dollars by 100 to get cents
  return dollars * 100;
}

export const renderProviderStatusColor = (s: ProviderStatus | string) => {
  switch (s) {
    case ProviderStatus.online:
      return lightTheme.green;
    case ProviderStatus.offline:
      return lightTheme.yellow;
    case ProviderStatus.unknown:
      return lightTheme.lightGray;
  }
};

export const renderProviderConnectionTypeName = (
  s: ProviderConnectionType | string
) => {
  switch (s) {
    case ProviderConnectionType.thirdParty:
      return 'KitchenHub';
    case ProviderConnectionType.direct:
      return 'Direct';
    case ProviderConnectionType.foodhub:
      return 'FoodHub';
  }
};

export const renderPosConnectionTypeName = (s: PosConnectionType | string) => {
  switch (s) {
    case PosConnectionType.foodhub:
      return 'FoodHub';
  }
};

export const renderProviderStatusName = (s: ProviderStatus | string) => {
  switch (s) {
    case ProviderStatus.online:
      return 'Online';
    case ProviderStatus.offline:
      return 'Offline';
    case ProviderStatus.unknown:
      return 'Unknown';
  }
};

export const renderProviderConnectionStatusIcon = (
  s: ProviderConnectionStatus | string
) => {
  switch (s) {
    case ProviderConnectionStatus.connected:
      return <ProviderConnectionConnectedIcon />;
    case ProviderConnectionStatus.waiting:
      return <ProviderConnectionPendingIcon />;
    case ProviderConnectionStatus.rejected:
      return <ProviderConnectionDisabledIcon />;
    case ProviderConnectionStatus.waitingMenu:
      return <ProviderConnectionPendingIcon />;
    case ProviderConnectionStatus.inProgress:
      return <ProviderConnectionPendingIcon />;
    case ProviderConnectionStatus.disabled:
      return <ProviderConnectionDisabledIcon />;
  }
};

export const renderProviderConnectionStatusName = (
  s: ProviderConnectionStatus | string
) => {
  switch (s) {
    case ProviderConnectionStatus.connected:
      return 'Connected';
    case ProviderConnectionStatus.waiting:
      return 'Waiting';
    case ProviderConnectionStatus.rejected:
      return 'Rejected';
    case ProviderConnectionStatus.waitingMenu:
      return 'Waiting Menu';
    case ProviderConnectionStatus.inProgress:
      return 'In Progress';
    case ProviderConnectionStatus.disabled:
      return 'Disabled';
    default:
      return '';
  }
};

export const RenderProviderStatus = ({
  statuses,
}: {
  statuses: { status: string }[];
}) => {
  if (!statuses || !statuses.length) return null;

  const priorityOrder = [
    ProviderStatus.unknown,
    ProviderStatus.offline,
    ProviderStatus.online,
  ];

  const matched = priorityOrder.find((status) =>
    statuses.some((item) => item.status === status)
  );

  return matched ? (
    <ProviderStatusComponent status={matched as ProviderStatus} />
  ) : null;
};

export const vDriveProviders = {
  UBER: 'uber',
  DOORDASH: 'doordash',
  RELAY: 'relay',
  ALLY: 'ally',
  CUTCATS: 'cutcats',
  DLIVRD: 'dlivrd',
  MOTOCLICK: 'motoclick',
  DEDICATED_DRIVER: 'dedicatedDriver',
  GRUBHUB: 'grubhub',
  DELIVERY_NOW: 'deliveryNow',
  REDWAGON: 'redwagon',
  VDRIVE_FLEET: 'vdriveFleet',
};

export const renderVDriveImage = (provider: VDriveProvider | string | null) => {
  switch (provider) {
    case vDriveProviders.UBER:
      return uberLogo;
    case vDriveProviders.DOORDASH:
      return doordashLogo;
    case vDriveProviders.RELAY:
      return relayLogo;
    case vDriveProviders.ALLY:
      return allyLogo;
    case vDriveProviders.CUTCATS:
      return cutcatsLogo;
    case vDriveProviders.DLIVRD:
      return dlivrdLogo;
    case vDriveProviders.MOTOCLICK:
      return isAdmin ? motoclickLogo : dspLogo;
    case vDriveProviders.DEDICATED_DRIVER:
      return dedicatedDriver;
    case vDriveProviders.GRUBHUB:
      return grubhubLogo;
    case vDriveProviders.REDWAGON:
      return redwagonLogo;
    case vDriveProviders.DELIVERY_NOW:
      return deliveryNowLogo;
    case vDriveProviders.VDRIVE_FLEET:
      return vdriveFleetLogo;
    default:
      return noFleetPhoto;
  }
};

export const renderVDriveIcon = (provider: VDriveProvider | string | null) => {
  switch (provider) {
    case vDriveProviders.UBER:
      return uberIcon;
    case vDriveProviders.DOORDASH:
      return doordashIcon;
    case vDriveProviders.RELAY:
      return relayIcon;
    case vDriveProviders.ALLY:
      return allyLogo;
    case vDriveProviders.CUTCATS:
      return cutcatsIcon;
    case vDriveProviders.DLIVRD:
      return dlivrdIcon;
    case vDriveProviders.MOTOCLICK:
      return isAdmin ? motoclickIcon : dspIcon;
    case vDriveProviders.DEDICATED_DRIVER:
      return dedicatedDriverIcon;
    case vDriveProviders.GRUBHUB:
      return grubhubIcon;
    case vDriveProviders.DELIVERY_NOW:
      return deliveryNowIcon;
    case vDriveProviders.REDWAGON:
      return redwagonIcon;
    case vDriveProviders.VDRIVE_FLEET:
      return vdriveFleetIcon;
    default:
      return noFleetPhoto;
  }
};

export const renderVDriveProviderName = (provider: VDriveProvider) => {
  switch (provider) {
    case VDriveProvider.uber:
      return 'Uber';
    case VDriveProvider.doordash:
      return 'DoorDash';
    case VDriveProvider.relay:
      return 'Relay';
    case VDriveProvider.ally:
      return 'Ally';
    case VDriveProvider.cutcats:
      return 'CutCats';
    case VDriveProvider.dlivrd:
      return 'Dlivrd';
    case VDriveProvider.motoclick:
      return 'MotoClick';
    case VDriveProvider.dedicatedDriver:
      return 'Dedicated Driver';
    case VDriveProvider.grubhub:
      return 'Grubhub';
    case VDriveProvider.deliveryNow:
      return 'DeliveryNow';
    case VDriveProvider.redwagon:
      return 'RedWagon';
    case VDriveProvider.vdriveFleet:
      return 'vDrive';
    default:
      return 'Unknown';
  }
};

/**
 * The fleet as orders and deliveries show it: the real provider, or, in demo
 * mode (see store/demoMode.ts), a plain DSP for every third-party fleet. A
 * dedicated driver is the restaurant's own and no fleet is no fleet, so both
 * stay as they are.
 */
const isMaskedFleet = (provider: VDriveProvider | string | null | undefined) =>
  !!provider &&
  provider !== vDriveProviders.DEDICATED_DRIVER &&
  isFleetHidden();

export const renderFleetImage = (
  provider: VDriveProvider | string | null | undefined
) => (isMaskedFleet(provider) ? dspLogo : renderVDriveImage(provider ?? null));

export const renderFleetIcon = (
  provider: VDriveProvider | string | null | undefined
) => (isMaskedFleet(provider) ? dspIcon : renderVDriveIcon(provider ?? null));

export const renderFleetName = (provider: VDriveProvider) =>
  isMaskedFleet(provider) ? 'DSP' : renderVDriveProviderName(provider);

export const renderFleetSource = (source: VDriveSource) =>
  source && isFleetHidden() ? 'vDrive' : renderVDriveSource(source);

export const renderProviderIcon = (provider: Provider | string, d?: string) => {
  switch (provider) {
    case Provider.ubereats:
      return uberIcon;
    case Provider.grubhub:
      return grubhubIcon;
    case Provider.vGrubs:
      return vGrubsProviderIcon;
    case Provider.doordash:
      return doordashIcon;
    case Provider.chownow:
      return chowNowIcon;
    case Provider.gloriafood:
      return gloriaIcon;
    case Provider.delivery:
      return deliveryIcon;
    case Provider.eatstreet:
      return eatstreetIcon;
    case Provider.menufy:
      return menufyIcon;
    case Provider.grubhubPos:
      return grubhubIcon;
    case Provider.doordashPos:
      return doordashIcon;
    case Provider.localForYou:
      return localForYouIcon;
    case Provider.nabe:
      return nabeIcon;
    case Provider.bytes:
      if (showsProviderIdentity()) {
        return bytesIcon;
      }
      return vGrubsAIIcon;
    case Provider.kanekt365:
      if (showsProviderIdentity()) {
        return kanekt365Icon;
      }
      return vGrubsAIIcon;
    case Provider.vGrubsAI:
      return vGrubsAIIcon;
    case Provider.hyperzod:
      return hyperzodIcon;
    case Provider.devourNow:
      return devourNowIcon;
    default:
      return d;
  }
};

export const renderProviderPhone = (p: Provider | string) => {
  switch (p) {
    case Provider.ubereats:
      return '+1 833 275 3287';
    case Provider.doordash:
      return '+1 855 973 1040';
    case Provider.grubhub:
      return '+1 877 585-7878';
    case Provider.vGrubs:
      return '+1 855 584 7827';
    case Provider.chownow:
      return '+1 888 707 2469';
    case Provider.gloriafood:
      return '+40 737 017 876';
    case Provider.delivery:
      return '+1 800 709 7191';
    case Provider.eatstreet:
      return '+1 866 654 8777';
    case Provider.menufy:
      return '+1 913 738 9399';
    case Provider.grubhubPos:
      return '+1 877 799 0790';
    case Provider.doordashPos:
      return '+1 855 973 1040';
    case Provider.localForYou:
      return '+61 1800 394 664';
    case Provider.nabe:
      return '+1 240 720 3456';
  }
};

export const renderIncomeStatusName = (
  p: IncomeStatus | AgentIncomeStatus | string
) => {
  switch (p) {
    case IncomeStatus.available:
      return 'Available';
    case IncomeStatus.pending:
    case AgentIncomeStatus.active:
      return 'Pending';
  }
};

export const renderIncomeTypeName = (
  p: IncomeType | AgentIncomeType | string,
  isAgent = false
) => {
  switch (p) {
    case IncomeType.bytes:
      return 'Bytes';
    case IncomeType.kanekt365:
      return 'Kanekt 365';
    case IncomeType.vGrubs:
      return 'vGrubs';
    case IncomeType.vOrders:
      return isAgent ? 'Monthly SaaS' : 'vOrders';
    case IncomeType.dcom:
      return 'Delivery.com';
    case IncomeType.vGrubsAI:
      return 'vGrubs AI';
    case IncomeType.balanceIncrementation:
      return 'Balance Addition';
    case IncomeType.balanceDeduction:
      return 'Balance Deduction';
    case AgentIncomeType.vDrive:
      return 'vDrive';
    case AgentIncomeType.flatAmount:
      return 'Flat Amount';
    case AgentIncomeType.pipeline:
      return 'Pipeline';
  }
};

export function showStringIfNotNull(
  str: string | null | undefined,
  comma = false
) {
  if (str) {
    return `${str}${comma ? ',' : ''}`;
  } else {
    return '';
  }
}

export const renderDayOfWeek = (day: number) => {
  switch (day) {
    case 0:
      return 'Sunday';
    case 1:
      return 'Monday';
    case 2:
      return 'Tuesday';
    case 3:
      return 'Wednesday';
    case 4:
      return 'Thursday';
    case 5:
      return 'Friday';
    case 6:
      return 'Saturday';

    default:
      break;
  }
};

export const renderDayOfWeekString = (day: DayOfWeek | WeekDay) => {
  switch (day.toLowerCase()) {
    case DayOfWeek.Sunday.toLowerCase():
      return 'Sunday';
    case DayOfWeek.Monday.toLowerCase():
      return 'Monday';
    case DayOfWeek.Tuesday.toLowerCase():
      return 'Tuesday';
    case DayOfWeek.Wednesday.toLowerCase():
      return 'Wednesday';
    case DayOfWeek.Thursday.toLowerCase():
      return 'Thursday';
    case DayOfWeek.Friday.toLowerCase():
      return 'Friday';
    case DayOfWeek.Saturday.toLowerCase():
      return 'Saturday';

    default:
      break;
  }
};

export const daysOfWeek = [
  { name: 'Sunday', value: 0 },
  { name: 'Monday', value: 1 },
  { name: 'Tuesday', value: 2 },
  { name: 'Wednesday', value: 3 },
  { name: 'Thursday', value: 4 },
  { name: 'Friday', value: 5 },
  { name: 'Saturday', value: 6 },
];

export const daysOfWeekString = [
  { name: 'Sunday', value: DayOfWeek.Sunday },
  { name: 'Monday', value: DayOfWeek.Monday },
  { name: 'Tuesday', value: DayOfWeek.Tuesday },
  { name: 'Wednesday', value: DayOfWeek.Wednesday },
  { name: 'Thursday', value: DayOfWeek.Thursday },
  { name: 'Friday', value: DayOfWeek.Friday },
  { name: 'Saturday', value: DayOfWeek.Saturday },
];

export const renderDayOfWeekFrom1 = (day: number) => {
  switch (day) {
    case 1:
      return 'Monday';
    case 2:
      return 'Tuesday';
    case 3:
      return 'Wednesday';
    case 4:
      return 'Thursday';
    case 5:
      return 'Friday';
    case 6:
      return 'Saturday';
    case 7:
      return 'Sunday';

    default:
      break;
  }
};

export const daysOfWeekFrom1 = [
  { name: 'Monday', value: 1 },
  { name: 'Tuesday', value: 2 },
  { name: 'Wednesday', value: 3 },
  { name: 'Thursday', value: 4 },
  { name: 'Friday', value: 5 },
  { name: 'Saturday', value: 6 },
  { name: 'Sunday', value: 7 },
];

// Define the function
export function firstWhereOrNull<T>(
  array: Array<T>,
  test: (element: T) => boolean
) {
  // Ensure the test argument is a function
  // if (typeof test !== 'function') {
  //   throw new TypeError('The test argument must be a function');
  // }

  // Iterate through the array
  for (const element of array) {
    // If the test function returns true for the current element, return the element
    if (test(element)) {
      return element;
    }
  }

  // If no element satisfies the test function, return null
  return null;
}

export const setTimeStartEnd = (
  d: Date | string | null,
  type: 'start' | 'end'
) => {
  const date = !d ? new Date() : d instanceof Date ? d : new Date(d);
  const dt = DateTime.fromJSDate(date);
  if (type === 'start') {
    return dt.startOf('day').toISO() as string; // stays in UTC
  }
  return dt.endOf('day').toISO() as string;
};

export const setTimeStartEndMonth = (
  d: Date | string,
  type: 'start' | 'end' | 'today'
) => {
  const date = d instanceof Date ? d : new Date(d);
  if (type === 'start') {
    // Return the start of the month with the start of the time
    return DateTime.fromJSDate(date)
      .startOf('month')
      .startOf('day')
      .toUTC()
      .toISO() as string;
  }

  if (type === 'end') {
    // Return the end of the month with the end of the time
    return DateTime.fromJSDate(date)
      .endOf('month')
      .endOf('day')
      .toUTC()
      .toISO() as string;
  }

  if (type === 'today') {
    return DateTime.fromJSDate(date).endOf('day').toUTC().toISO() as string;
  }

  return '';
};

export const dateIsToday = (dateToCheck: string) => {
  const date = DateTime.fromISO(dateToCheck).toISODate();
  const today = DateTime.local().toISODate();

  return date === today;
};

export const renderDateLabelWithToday = (from: string, to: string) => {
  const dateFrom = DateTime.fromISO(from).toFormat('MMM dd, yyyy');
  const dateTo = DateTime.fromISO(to).toFormat('MMM dd, yyyy');

  if (dateIsToday(from) && dateIsToday(to)) {
    return 'Today';
  } else return `${dateFrom} - ${dateTo}`;
};

// Date label used in export file names. Same "MMM dd, yyyy" format as the
// export modal, but collapses a single day (including "Today") to one date
// instead of a range.
export const renderDateLabelForFileName = (from: string, to: string) => {
  const dateFrom = DateTime.fromISO(from).toFormat('MMM dd, yyyy');
  const dateTo = DateTime.fromISO(to).toFormat('MMM dd, yyyy');

  if (dateFrom === dateTo) return dateFrom;
  return `${dateFrom} - ${dateTo}`;
};

export const RenderBrandType = (t: BrandType) => {
  switch (t) {
    case BrandType.vGrubs:
      return 'vGrubs';
    case BrandType.RB:
      return 'Restaurant Brand';
    case BrandType.TDOC:
      return 'TODC';
    case BrandType.VPC:
      return 'VPC';
    case BrandType.VDC:
      return 'VDC';
    case BrandType.FJ:
      return 'FJ';
    case BrandType.TLB:
      return 'The Licking Brand';

    default:
      break;
  }
};

export function unGroupFundingTipSchedule(
  data: FundedTipScheduleEntity[]
): ScheduleEntity[] {
  return data.flatMap((day) =>
    day.times.map((time) => ({
      dayOfTheWeek: day.dayOfTheWeek,
      amount: time.amount,
      startTime: time.startTime,
      endTime: time.endTime,
    }))
  );
}

export function groupFundingTipSchedule(data: ScheduleEntity[]) {
  return data.reduce(
    (result: FundedTipScheduleEntity[], item: ScheduleEntity) => {
      const dayIndex = result.findIndex(
        (day: FundedTipScheduleEntity) => day.dayOfTheWeek === item.dayOfTheWeek
      );

      if (dayIndex > -1) {
        result[dayIndex].times.push({
          amount: item.amount,
          startTime: item.startTime,
          endTime: item.endTime,
        });
      } else {
        result.push({
          dayOfTheWeek: item?.dayOfTheWeek || 0,
          times: [
            {
              amount: item.amount,
              startTime: item.startTime,
              endTime: item.endTime,
            },
          ],
        });
      }

      return result;
    },
    []
  );
}

export const isTimeFromEarlier = (timeFrom: string, timeTo: string) => {
  const [fromHours, fromMinutes] = timeFrom.split(':').map(Number);
  const [toHours, toMinutes] = timeTo.split(':').map(Number);

  if (fromHours < toHours) {
    return true;
  }
  if (fromHours === toHours && fromMinutes < toMinutes) {
    return true;
  }
  return false;
};

export const getTimeAgoTimeIn = (isoDate?: string | undefined) => {
  if (!isoDate) return null;

  const date = DateTime.fromISO(isoDate).toLocal();
  const diff = date
    .diffNow(['years', 'months', 'days', 'hours', 'minutes', 'seconds'])
    .toObject();

  const getTimeString = (value: number, unit: string) => {
    const absValue = Math.abs(value);
    return `${absValue} ${unit}${absValue > 1 ? 's' : ''}`;
  };

  if (diff.years)
    return diff.years > 0
      ? `in ${getTimeString(diff.years, 'year')}`
      : `${getTimeString(diff.years, 'year')} ago`;
  if (diff.months)
    return diff.months > 0
      ? `in ${getTimeString(diff.months, 'month')}`
      : `${getTimeString(diff.months, 'month')} ago`;
  if (diff.days)
    return diff.days > 0
      ? `in ${getTimeString(diff.days, 'day')}`
      : `${getTimeString(diff.days, 'day')} ago`;
  if (diff.hours)
    return diff.hours > 0
      ? `in ${getTimeString(diff.hours, 'hour')}`
      : `${getTimeString(diff.hours, 'hour')} ago`;
  if (diff.minutes)
    return diff.minutes > 0
      ? `in ${getTimeString(diff.minutes, 'minute')}`
      : `${getTimeString(diff.minutes, 'minute')} ago`;

  return 'Just now';
};

export const isFutureDate = (isoDate?: string | null | undefined) => {
  if (!isoDate) return false; // Handle invalid inputs

  const inputDate = DateTime.fromISO(isoDate);
  const now = DateTime.now();

  return inputDate > now;
};

export const downloadBase64PDF = (
  base64String: string,
  fileName = 'document.pdf'
) => {
  if (!base64String) {
    return;
  }

  // Convert base64 to Blob
  const byteCharacters = atob(base64String);
  const byteNumbers = new Uint8Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const blob = new Blob([byteNumbers], { type: 'application/pdf' });

  // Create a download link
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

const UTF8_BOM = new Uint8Array([0xef, 0xbb, 0xbf]);

export const downloadBase64CSV = (
  base64String: string,
  fileName = 'document.csv'
): Promise<void> => {
  if (!base64String) {
    return Promise.resolve();
  }

  // Decode base64 to bytes (preserves UTF-8 from server)
  const byteCharacters = atob(base64String);
  const byteNumbers = new Uint8Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  // Prepend UTF-8 BOM so Excel and other tools open as UTF-8 and format correctly
  const withBom = new Uint8Array(UTF8_BOM.length + byteNumbers.length);
  withBom.set(UTF8_BOM, 0);
  withBom.set(byteNumbers, UTF8_BOM.length);

  const blob = new Blob([withBom], { type: 'text/csv;charset=utf-8' });

  // Create a download link
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);

  return Promise.resolve();
};

export const downloadImageFromURL = async (
  imageUrl: string | null | undefined,
  fileName = 'downloaded-image.png'
) => {
  try {
    if (!imageUrl) return;
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName; // This forces download instead of opening in browser
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the object URL
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Error downloading image:', error);
  }
};

export async function fetchAndDownloadCsvFile(
  url: string,
  fileNameOverride?: string
) {
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    // ✅ Extract and decode filename (default = last URL segment)
    const fileName =
      fileNameOverride ??
      decodeURIComponent(url.substring(url.lastIndexOf('/') + 1));

    // ✅ Trigger download
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href); // Clean up
  } catch (error) {
    console.error('Failed to download file:', url, error);
  }
}

export function removeHyphens(str: string) {
  return str.replace(/-/g, '');
}

export const MULTIPART_HEADERS = {
  headers: { 'content-type': 'multipart/form-data' },
};

export const hasTwoWeeksPassed = (date: string) => {
  // Parse the provided date
  const providedDate = DateTime.fromISO(date); // Adjust the parsing method as needed

  // Add two weeks to the provided date
  const twoWeeksLater = providedDate.plus({ weeks: 2 });

  // Compare with the current date
  return DateTime.now() > twoWeeksLater;
};

export const generateRandomId = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min) + min);
};

export function grantNotificationPermission() {
  if (!('Notification' in window)) {
    return console.warn('Browser does not support notifications');
  } else if (Notification.permission === 'granted') {
    return;
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission();
  }
}

export function triggerNotification(text = 'Notification', bodyText: string) {
  if (!('Notification' in window)) {
    return console.warn('Browser does not support notifications');
  } else if (Notification.permission === 'granted') {
    new Notification(text, {
      body: bodyText,
      icon: vGrubsProviderLogo,
    });
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission();
  }
}

export const renderStoreName = (storeName: MenuNamesResponse) =>
  `${storeName.locationName} ${
    storeName.brandName && `/ ${storeName.brandName}`
  }`;

export const handlePartnerAuthNavigation = (
  userData: any,
  finalRedirect = true
) => {
  const { user, incompleteLocation, locationsAmount } = userData;

  const hasLocations = locationsAmount > 0;

  if (!user) return (window.location.href = ADMIN_LOGIN);

  if (user.role === UserRole.manager) {
    if (user.termsAccepted) return (window.location.href = ADMIN_DASHBOARD);
    else return (window.location.href = PARTNER_SIGN_UP_TERMS);
  }

  // Staff don't own locations, so everything below this point - the merchant
  // onboarding chain - is not theirs to walk. Chat is the only page they can
  // reach in this app.
  if (user.role === UserRole.staff) {
    if (user.termsAccepted) return (window.location.href = PARTNER_CHAT);
    else return (window.location.href = PARTNER_SIGN_UP_TERMS);
  }

  if (!user.completed)
    return (window.location.href = PARTNER_SIGN_UP_USER_INFO);

  if (!user.termsAccepted)
    return (window.location.href = PARTNER_SIGN_UP_TERMS);

  if (incompleteLocation !== null) {
    if (!incompleteLocation.confirmed) {
      return (window.location.href = hasLocations
        ? PARTNER_LOCATIONS_CONFIRM_LOCATION
        : PARTNER_SIGN_UP_CONFIRM_LOCATION);
    }
    if (
      incompleteLocation.menuSignInProviders.length < 1 &&
      !incompleteLocation.menuSignInProvidersSkipped
    ) {
      // The POS step sits before the marketplaces and its question is answered
      // once: land there only while "Do you use a POS system?" is unanswered,
      // so a partner who picked a POS but has not granted access yet is not
      // held on that step (Settings lets them finish later).
      const posAnswered = incompleteLocation.integrationAccess?.usesPos != null;
      if (hasLocations) {
        window.location.href = posAnswered
          ? PARTNER_LOCATIONS_SELECT_PROVIDERS
          : PARTNER_LOCATIONS_POS_SETUP;
      } else {
        window.location.href = posAnswered
          ? PARTNER_SIGN_UP_SELECT_PROVIDERS
          : PARTNER_SIGN_UP_POS_SETUP;
      }
      return;
    }
    window.location.href = hasLocations
      ? PARTNER_LOCATIONS_BUSINESS_LICENSE
      : PARTNER_SIGN_UP_BUSINESS_LICENSE;
    return;
  }
  if (locationsAmount === 0) {
    return (window.location.href = PARTNER_SIGN_UP_ADD_LOCATION);
  }
  if (finalRedirect) {
    window.location.href = '/';
  }
  return;
};

export function isEmailValid(email: string) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.toLowerCase());
}

export function isLatitudeValid(lat: string) {
  const value = parseFloat(lat);
  return !isNaN(value) && value >= -90 && value <= 90;
}

export function isLongitudeValid(lng: string) {
  const value = parseFloat(lng);
  return !isNaN(value) && value >= -180 && value <= 180;
}
export const handleChangeNumInputNoDecimalsMinMax = (
  e: any,
  setState: any,
  min = 0,
  max = 100
) => {
  const input = e.target.value;

  // Allow empty input or only digits (no decimals)
  if (/^\d*$/.test(input)) {
    const intValue = input === '' ? '' : parseInt(input, 10);

    // Allow value between 0 and 200
    if (intValue === '' || (intValue >= min && intValue <= max)) {
      setState(input);
    }
  }
};

export const handleChangeNumInputTwoDecimalsMinMax = (
  e: any,
  setState: any,
  min = 0,
  max = 100
) => {
  const input = e.target.value;

  // Allow empty input or valid number with up to 2 decimals
  if (/^\d*\.?\d{0,2}$/.test(input)) {
    const floatValue = input === '' ? '' : parseFloat(input);

    // Allow value between min and max, or empty
    if (floatValue === '' || (floatValue >= min && floatValue <= max)) {
      setState(input);
    }
  }
};

export const numberInputOnWheelPreventChange = (e: any) => {
  // Prevent the input value change
  e.target.blur();

  // Prevent the page/container scrolling
  e.stopPropagation();

  // Refocus immediately, on the next tick (after the current
  // function is done)
  setTimeout(() => {
    e.target.focus();
  }, 0);
};

export const useBackButtonHandler = (onBack: () => void) => {
  const location = useLocation();

  useEffect(() => {
    const handlePopState = () => {
      onBack();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location]);
};

export function copyToClipboard(value: string | null | undefined) {
  // Copy the text inside the text field
  navigator.clipboard.writeText(value ?? '');

  // Alert the copied text
  SuccessToast('Copied to clipboard');
}

export const renderAdvancedQueryTypesOptionLabel = (
  value: AdvancedQueryTypes
) => {
  const advancedQueryTypesLabel: {
    value: AdvancedQueryTypes;
    name: string;
  }[] = [
    { value: AdvancedQueryTypes.ALL, name: 'All Locations & Brands' },
    { value: AdvancedQueryTypes.ALL_TDOC_ONLY, name: 'All TODC Only' },
    { value: AdvancedQueryTypes.ALL_VPC_ONLY, name: 'All VPC Only' },
    { value: AdvancedQueryTypes.ALL_VDC_ONLY, name: 'All VDC Only' },
    { value: AdvancedQueryTypes.ALL_FJ_ONLY, name: 'All FJ Only' },
    {
      value: AdvancedQueryTypes.ALL_TLB_ONLY,
      name: 'All The Licking Brands Only',
    },
    {
      value: AdvancedQueryTypes.ALL_LOCATIONS,
      name: 'All Locations + Location Brands (without vGrubs)',
    },
    {
      value: AdvancedQueryTypes.ALL_RB_ONLY,
      name: 'All Restaurant Brands Only',
    },
    {
      value: AdvancedQueryTypes.ALL_V_GRUBS_ONLY,
      name: 'All vGrubs  Only',
    },
    {
      value: AdvancedQueryTypes.LOCATION_BRAND_SPECIFIC,
      name: 'Location Brand Specific',
    },
    {
      value: AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_ANY_BRAND,
      name: 'Location Specific (Location without any brand)',
    },
    {
      value: AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_V_GRUBS,
      name: 'Location Specific + Location Brands (without vGrubs)',
    },
    {
      value: AdvancedQueryTypes.LOCATION_SPECIFIC_WITH_V_GRUBS,
      name: 'Location Specific + vGrubs Brands Only',
    },
    {
      value: AdvancedQueryTypes.LOCATION_SPECIFIC_ONLY_V_GRUBS,
      name: 'Location Specific (vGrubs revenue only)',
    },
    {
      value: AdvancedQueryTypes.V_GRUBS_BRAND_SPECIFIC,
      name: 'vGrubs Brand Specific',
    },
  ];

  return advancedQueryTypesLabel.find((r) => r.value === value)?.name;
};

export const renderAdvancedQueryTypesOptionLabelForPartner = (
  value: AdvancedQueryTypes
) => {
  const advancedQueryTypesLabel = [
    // 🏢 All Locations
    { value: AdvancedQueryTypes.ALL, name: 'All Locations & Brands' },

    // 🍽 By Brand
    {
      value: AdvancedQueryTypes.ALL_V_GRUBS_ONLY,
      name: 'All vGrubs Only',
    },
    {
      value: AdvancedQueryTypes.ALL_TDOC_ONLY,
      name: 'All TODC Only',
    },
    {
      value: AdvancedQueryTypes.ALL_VPC_ONLY,
      name: 'All VPC Only',
    },
    {
      value: AdvancedQueryTypes.ALL_VDC_ONLY,
      name: 'All VDC Only',
    },
    {
      value: AdvancedQueryTypes.ALL_FJ_ONLY,
      name: 'All FJ Only',
    },
    {
      value: AdvancedQueryTypes.ALL_RB_ONLY,
      name: 'All Restaurant Brands Only',
    },
    {
      value: AdvancedQueryTypes.ALL_TLB_ONLY,
      name: 'All The Licking Brands',
    },

    // 📍 Specific Locations
    {
      value: AdvancedQueryTypes.LOCATION_BRAND_SPECIFIC,
      name: 'Location Brand Specific',
    },
    {
      value: AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_ANY_BRAND,
      name: 'Location Specific (Location without any brand)',
    },
    {
      value: AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_V_GRUBS,
      name: 'Location Specific + My Brands',
    },
    {
      value: AdvancedQueryTypes.LOCATION_SPECIFIC_WITH_V_GRUBS,
      name: 'Location Specific + vGrubs Brands Only',
    },
    {
      value: AdvancedQueryTypes.V_GRUBS_BRAND_SPECIFIC,
      name: 'vGrubs Brand Specific',
    },
  ];

  return advancedQueryTypesLabel.find((r) => r.value === value)?.name;
};

export const formatDateByGroupByTypeChart = (
  date: string,
  groupByType: GroupByType,
  long?: boolean
) => {
  if (groupByType === GroupByType.HOUR) {
    return DateTime.fromISO(date).toFormat('HH:mm a');
  }
  if (groupByType === GroupByType.DAY) {
    return long
      ? DateTime.fromISO(date).toFormat('MMM d, yyyy')
      : DateTime.fromISO(date).toFormat('MMM d');
  }
  if (groupByType === GroupByType.MONTH) {
    return DateTime.fromISO(date).toFormat('MMM yyyy');
  }
  if (groupByType === GroupByType.YEAR) {
    return DateTime.fromISO(date).toFormat('yyyy');
  }

  return DateTime.fromISO(date).toFormat('yyyy');
};

export function getGroupByType(fromISO: string, toISO: string): GroupByType {
  const fromDate = DateTime.fromISO(fromISO);
  const toDate = DateTime.fromISO(toISO);

  const diffInDays = toDate.diff(fromDate, 'days').days;
  const diffInMonths = toDate.diff(fromDate, 'months').months;
  // const diffInYears = toDate.diff(fromDate, 'years').years;

  if (diffInDays < 1) {
    return GroupByType.HOUR;
  } else if (diffInDays <= 31) {
    return GroupByType.DAY;
  } else if (diffInMonths <= 12) {
    return GroupByType.MONTH;
  } else {
    return GroupByType.YEAR;
  }
}

export const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export const renderMiles = (m: number) => {
  if (m === 1) {
    return 'mile';
  } else return 'miles';
};

export function sanitizeDeliveryAssignment(
  deliveryAssignment: DeliveryAssignmentEntity
): DeliveryAssignmentEntity {
  return {
    ...deliveryAssignment,
    providers: deliveryAssignment.providers.map(({ id, ...rest }) => rest),
    nonOperatingConditions: deliveryAssignment.nonOperatingConditions.map(
      ({ id, ...rest }) => rest
    ),
    times: deliveryAssignment.times.map(({ id, ...rest }) => rest),
  };
}

// helper formatter
export const formatSSN = (value: string) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 9);
  const parts: string[] = [];

  if (digits.length > 0) parts.push(digits.slice(0, 3));
  if (digits.length > 3) parts.push(digits.slice(3, 5));
  if (digits.length > 5) parts.push(digits.slice(5, 9));

  return parts.join('-');
};

export const formatEIN = (value: string) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 9); // EIN = 9 digits too
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
};

export function calculatePercentageChange(
  current: number,
  previous: number
): string {
  if (previous === 0) {
    // Avoid division by zero
    return current === 0 ? '+0.0%' : '+100.0%';
  }

  const change = ((current - previous) / previous) * 100;
  const formatted = change.toFixed(1); // one decimal place
  return (change >= 0 ? '+' : '') + formatted + '%';
}

export enum NumberType {
  USD = 'USD',
  NUMBER = 'NUMBER',
  MINUTES = 'MINUTES',
}

export const formatAmount = (
  amount: number,
  currency: NumberType,
  short = false
) => {
  switch (currency) {
    case NumberType.USD:
      return short
        ? displayCentsInDollarsShort(amount)
        : displayCentsInDollars(amount);
    case NumberType.NUMBER:
      return short
        ? formatNumber(amount, {
            notation: 'compact',
            compactDisplay: 'short',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })
        : formatNumber(amount);
    case NumberType.MINUTES:
      return short ? `${amount}'` : `${amount} min`;
  }
};

export const getTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const isKitchenHubProviderConnection = (provider: Provider) =>
  provider !== Provider.gloriafood &&
  provider !== Provider.grubhub &&
  provider !== Provider.doordash &&
  provider !== Provider.ubereats &&
  provider !== Provider.delivery &&
  provider !== Provider.vGrubs &&
  provider !== Provider.nabe &&
  provider !== Provider.localForYou;

export const isDirectProviderConnection = (provider: Provider) =>
  provider === Provider.delivery ||
  provider === Provider.vGrubs ||
  provider === Provider.nabe ||
  provider === Provider.bytes ||
  provider === Provider.vGrubsAI ||
  provider === Provider.hyperzod ||
  provider === Provider.devourNow;

export const isFoodHubProviderConnection = (provider: Provider) =>
  provider === Provider.gloriafood ||
  provider === Provider.grubhub ||
  provider === Provider.doordash ||
  provider === Provider.ubereats ||
  provider === Provider.localForYou;

export const renderProtocol = (protocol: PROTOCOL) => {
  switch (protocol) {
    case PROTOCOL.rtp:
      return 'In Minutes';
    case PROTOCOL.ach:
      return '1 Day';
    case PROTOCOL.standard:
      return 'Standard';
  }
};

export const renderVDriveSource = (source: VDriveSource) => {
  switch (source) {
    case VDriveSource.firstdelivery:
      return 'First Delivery';
    case VDriveSource.cartwheel:
      return 'Cartwheel';
    case VDriveSource.motoclick:
      return isAdmin ? 'Motoclick' : 'vDrive';
    case VDriveSource.nash:
      return 'Nash';
    case VDriveSource.vdriveFleet:
      return 'vDrive';
  }
};

/**
 * The reason a source gave no quote, for the Fleet Assign History tooltip.
 * Accepts a plain string or `{ message }`, since the two specs disagree.
 */
export const renderQuoteHistoryError = (
  error: OrderVDriveQuoteHistoryEntity['error']
) => (typeof error === 'string' ? error : error?.message) || '';

export const menuItemNameMaxLength = 500;
export const menuItemDescriptionMaxLength = 1000;

export const validateMenuItemName = (name: string) => {
  if (name.length > menuItemNameMaxLength)
    return ErrorToast('Name must be less than 500 characters');
  return true;
};

export const validateMenuItemDescription = (description: string) => {
  if (description.length > menuItemDescriptionMaxLength)
    return ErrorToast('Description must be less than 1000 characters');
  return true;
};

const MOBILE_BREAKPOINT = 800;

export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);

    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    // Set initial value
    setIsMobile(mediaQuery.matches);

    // Listen for changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Safari < 14
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  return isMobile;
};

export const virtualizerStyle = (
  virtualRow: VirtualItem,
  rowVirtualizer: Virtualizer<Window, Element>
) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  minHeight: `${virtualRow.size}px`,
  transform: `translateY(${
    virtualRow.start - rowVirtualizer.options.scrollMargin
  }px)`,
});

export const isUrlValid = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};
